import type { TagsApproval } from '../types/tags'

// A GCP-onboarded application inherits its GCP Project's tag record instead of getting its
// own, so one record carries approvals for the project and for every application under it.
// Entries are told apart by the `application` field: project level entries have none.
export const PROJECT_APPROVAL_NAMES = ['FinanceTeam', 'CloudEconomicsTeam']
export const APPLICATION_APPROVAL_NAMES = ['PlatformOwner', 'ApplicationOwner']

const pendingApproval = (
	name: string,
	application?: string
): TagsApproval => ({
	name,
	status: null,
	approverID: '',
	lastUpdated: '',
	...(application ? { application } : {})
})

export const isProjectLevelApproval = (approval: TagsApproval) =>
	!approval.application

export const isApprovalForApplication = (
	approval: TagsApproval,
	applicationName: string
) => Boolean(applicationName) && approval.application === applicationName

/**
 * Approvals as the GCP Project Page sees them: exactly what was there before applications
 * started sharing the record.
 */
export const getProjectLevelApprovals = (
	approvalStatus: TagsApproval[] = []
): TagsApproval[] => approvalStatus.filter(isProjectLevelApproval)

/**
 * Approvals as a single application request sees them: its own PlatformOwner and
 * ApplicationOwner entries, plus the project level Finance and Cloud Economics approvals it
 * inherits. The project level PlatformOwner is deliberately excluded — the same person
 * already has a row here for this request — and other applications' entries never appear.
 */
export const getApplicationApprovals = (
	approvalStatus: TagsApproval[] = [],
	applicationName: string
): TagsApproval[] => {
	const own = APPLICATION_APPROVAL_NAMES.map((name) =>
		approvalStatus.find(
			(approval) =>
				approval.name === name &&
				isApprovalForApplication(approval, applicationName)
		)
	).filter((approval): approval is TagsApproval => Boolean(approval))

	const inherited = PROJECT_APPROVAL_NAMES.map((name) =>
		approvalStatus.find(
			(approval) => approval.name === name && isProjectLevelApproval(approval)
		)
	).filter((approval): approval is TagsApproval => Boolean(approval))

	return [...own, ...inherited]
}

/**
 * Pending approvers for a new application funding record. Status is not copied from the
 * parent. Platform Owner and Application Owner carry the application name so a record that
 * still shares the project tag can tell them apart from the project rows.
 */
export const getApplicationFundingApprovals = (
	applicationName = ''
): TagsApproval[] => [
	pendingApproval('PlatformOwner', applicationName),
	pendingApproval('FinanceTeam'),
	pendingApproval('CloudEconomicsTeam'),
	pendingApproval('ApplicationOwner', applicationName)
]

export const resetTagsApprovalStatus = (
	approvalStatus: TagsApproval[] = []
): TagsApproval[] =>
	approvalStatus.map((approval) => ({
		...approval,
		status: null,
		approverID: '',
		lastUpdated: ''
	}))

/**
 * Resets only the approvals a given application owns, leaving the project level entries and
 * every other application's entries untouched. Editing tags from one application request must
 * not re-open approvals that belong to the project or to its neighbours.
 */
export const resetApplicationApprovalStatus = (
	approvalStatus: TagsApproval[] = [],
	applicationName: string
): TagsApproval[] =>
	approvalStatus.map((approval) =>
		isApprovalForApplication(approval, applicationName)
			? { ...approval, status: null, approverID: '', lastUpdated: '' }
			: approval
	)

/**
 * True when every approval this application depends on is approved. Filtering by application
 * is required: evaluated over the raw list, every other pending application in the project
 * would block this one.
 */
export const areApplicationTagsApproved = (
	approvalStatus: TagsApproval[] = [],
	applicationName = ''
): boolean => {
	const required = getApplicationApprovals(approvalStatus, applicationName)

	return (
		required.length ===
			APPLICATION_APPROVAL_NAMES.length + PROJECT_APPROVAL_NAMES.length &&
		required.every((approval) => approval.status === 1)
	)
}

export const getTagApproverLabel = (name: string) =>
	({
		PlatformOwner: 'Platform Owner',
		ApplicationOwner: 'Application Owner',
		FinanceTeam: 'Finance Team',
		CloudEconomicsTeam: 'Cloud Economics Team',
		CloudTribe: 'Cloud Tribe'
	}[name] ?? name)

export const getApproverEmail = (email: unknown): string | undefined => {
	const value = typeof email === 'string' ? email.trim() : ''

	return value && value.toUpperCase() !== 'N/A' ? value : undefined
}

/**
 * The email that must be signed in to approve Platform Owner or Application Owner.
 * Finance and Cloud Economics return undefined and keep their team-permission check.
 */
export const getRequiredApproverEmail = (
	approval: TagsApproval,
	tagDetails: Record<string, any> = {}
): string | undefined => {
	if (approval.name === 'ApplicationOwner') {
		const applications: Array<Record<string, any>> =
			tagDetails.applications || []
		const matched = approval.application
			? applications.find(
					(application) => application.applicationName === approval.application
			  )
			: applications.length === 1
			? applications[0]
			: undefined

		return (
			getApproverEmail(matched?.applicationOwnerEmail) ||
			getApproverEmail(tagDetails.applicationOwnerEmail)
		)
	}

	if (approval.name === 'PlatformOwner') {
		return getApproverEmail(tagDetails.platformOwnerEmail)
	}

	return undefined
}
